import { FC, RefObject, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

interface ScrollProps {
  scrollRef: RefObject<HTMLElement | null>
  data?: any[]
}

const ScrollToggle: FC<ScrollProps> = ({ scrollRef, data }) => {
  const { pathname } = useLocation()
  const [isAtBottom, setIsAtBottom] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const container = scrollRef.current
    if (!container) return

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container
      const isScrollable = scrollHeight > clientHeight
      setIsVisible(scrollTop > 50 || isScrollable)
      setIsAtBottom(scrollTop + clientHeight >= scrollHeight - 20)
    }

    const resizeObserver = new ResizeObserver(() => {
      handleScroll()
    })

    resizeObserver.observe(container)
    container.addEventListener('scroll', handleScroll)
    handleScroll()

    return () => {
      container.removeEventListener('scroll', handleScroll)
      resizeObserver.disconnect()
    }
  }, [pathname, scrollRef, data])

  useEffect(() => {
    scrollRef.current?.scrollTo(0, 0)
  }, [pathname, scrollRef])

  const toggleScroll = () => {
    const container = scrollRef.current
    if (!container) return

    if (isAtBottom) {
      container.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' })
    }
  }

  return (
    <button
      type="button"
      onClick={toggleScroll}
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '90px',
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        zIndex: 1050,
        opacity: isVisible ? 1 : 0,
        visibility: isVisible ? 'visible' : 'hidden',
        transform: isVisible ? 'scale(1)' : 'scale(0.8)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          transform: isAtBottom ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.4s ease',
        }}
      >
        <path d="M7 13l5 5 5-5M7 6l5 5 5-5" />
      </svg>
    </button>
  )
}

export default ScrollToggle
